// ============================================================================
// ONBOARDING — Persona & Module Types
// ============================================================================

/**
 * Business persona types for TMS tenant onboarding.
 * Determines module tier layout, dashboard presets, and onboarding flow.
 *
 * Adding a new persona requires:
 * 1. Add enum value here
 * 2. Add PersonaDefinition in persona-definitions.ts config
 * 3. Add i18n keys
 * Zero code changes needed.
 */
export enum PersonaType {
  BROKER = 'BROKER',
  CARRIER = 'CARRIER',
  LSP_3PL = 'LSP_3PL',
  PRIVATE_FLEET = 'PRIVATE_FLEET',
  HYBRID = 'HYBRID',
}

/**
 * Module identifiers matching navigation-config-mvp.ts top-level items.
 * Used for Core/Available tier assignment per persona.
 */
export enum ModuleId {
  DASHBOARD = 'dashboard',
  ORDERS = 'orders',
  LOAD_REQUESTS = 'load-requests',
  SHIPMENTS = 'shipments',
  DISPATCH = 'dispatch',
  TRIPS = 'trips',
  TRACKING = 'tracking',
  ROUTES = 'routes',
  CARRIERS = 'carriers',
  RATES = 'rates',
  CUSTOMERS = 'customers',
  CUSTOMER_PORTAL = 'customer-portal',
  ZONES = 'zones',
  CONSOLIDATION = 'consolidation',
  LOGISTICS = 'logistics',
  FLEET = 'fleet',
  DRIVERS = 'drivers',
  MAINTENANCE = 'maintenance',
  FUEL_TRACKING = 'fuel-logs',
  INSPECTIONS = 'inspections',
  HOS = 'hos',
  CARRIER_SAFETY = 'carrier-safety',
  INVOICES = 'invoices',
  PAYMENTS = 'payments',
  EXPENSES = 'expenses',
  CONTRACTS = 'contracts',
  CLAIMS = 'claims',
  FACTORING = 'factoring',
  DRIVER_SETTLEMENTS = 'driver-settlements',
  DOCUMENTS = 'documents',
  REPORTS = 'reports',
  LOAD_BOARD = 'load-postings',
}

/**
 * Module visibility tier within the sidebar.
 * - core: Top section, dashboard widgets auto-included
 * - available: "More" section, accessible but not prominent
 */
export type ModuleTier = 'core' | 'available';

/**
 * How the user selected their persona during onboarding.
 */
export enum PersonaSelectionMethod {
  CARD = 'card',
  AI = 'ai',
  QUIZ = 'quiz',
}

/**
 * Margin/commission type for broker and 3PL tenants.
 */
export enum MarginType {
  FLAT = 'FLAT',
  PERCENTAGE = 'PERCENTAGE',
}

/**
 * Default rate structure shown in onboarding quiz. Narrower option list than
 * the canonical `RateStructure` (contract-rates.types) — PER_PALLET is
 * considered too niche for the first-run carrier quiz. Keep the two TS enums
 * and Postgres enum types distinct so the tenant_rate_defaults column doesn't
 * widen implicitly.
 */
export enum OnboardingRateStructure {
  PER_MILE = 'PER_MILE',
  FLAT_RATE = 'FLAT_RATE',
  PER_CWT = 'PER_CWT',
}

/**
 * Fuel surcharge calculation method used by the onboarding quiz. Narrower
 * option list than the canonical `FuelSurchargeType` (contract-rates.types) —
 * `NONE` doesn't make sense when the question is "do you charge one?" (answer
 * is captured by leaving the field blank). See note on `OnboardingRateStructure`.
 */
export enum OnboardingFuelSurchargeType {
  FIXED_PERCENTAGE = 'FIXED_PERCENTAGE',
  DOE_INDEXED = 'DOE_INDEXED',
  INCLUDED = 'INCLUDED',
}

// ============================================================================
// Onboarding Config Interfaces (used by persona-definitions.ts)
// ============================================================================

/**
 * Connect card types shown in onboarding Step 3.
 */
export enum ConnectCardType {
  ELD = 'ELD',
  LOAD_BOARD = 'LOAD_BOARD',
  FACTORING = 'FACTORING',
  WMS = 'WMS',
  CUSTOMS_BROKER = 'CUSTOMS_BROKER',
}

/**
 * Dynamic form field rendered in persona-specific Step 3.
 *
 * `label` / `description` remain English fallbacks for backwards compatibility.
 * `labelKey` / `descriptionKey` are i18n keys resolved on the frontend (preferred
 * when present). New fields should always populate the *Key variants so the
 * Step 3 form renders in the user's locale; legacy callers that only supply
 * `label` continue to work via the fallback path.
 */
export interface PersonaSetupField {
  id: string;
  type: 'select' | 'radio' | 'checkbox-group' | 'text';
  /** English fallback label */
  label: string;
  /** Translation key under the `onboarding` namespace (e.g. `personaSetup.fields.freightTypes.label`) */
  labelKey?: string;
  required: boolean;
  options?: PersonaSetupFieldOption[];
  dependsOn?: {
    field: string;
    value: string | boolean;
  };
  saveTo: string;
}

export interface PersonaSetupFieldOption {
  value: string;
  /** English fallback label */
  label: string;
  /** Translation key under the `onboarding` namespace */
  labelKey?: string;
  /** English fallback description */
  description?: string;
  /** Translation key under the `onboarding` namespace */
  descriptionKey?: string;
  icon?: string;
}

/**
 * Action card shown on the Welcome screen (Step 4).
 */
export interface GetStartedAction {
  titleKey: string;
  descriptionKey?: string;
  href: string;
  icon?: string;
}

/**
 * Quiz question for persona determination fallback.
 */
export interface QuizQuestion {
  id: string;
  questionKey: string;
  type: 'yes_no' | 'single_choice' | 'multi_choice';
  options?: PersonaSetupFieldOption[];
  weight: number;
  showIf?: {
    questionId: string;
    answer: string | boolean;
  };
}

/**
 * Rule for mapping quiz answers to a persona.
 */
export interface QuizPersonaRule {
  personaId: PersonaType;
  conditions: QuizCondition[];
  minScore: number;
}

export interface QuizCondition {
  questionId: string;
  answer: string | boolean;
  weight: number;
}
